"use client";

import classNames from "classnames";
import type { ReactNode } from "react";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { twMerge } from "tailwind-merge";

import { ScrollView } from "@/component/ScrollView";

import styles from "./index.module.css";

interface SectionClassNames {
  mask?: string;
  content?: string;
}

export interface FullScreenModalProps {
  children: ReactNode;
  open?: boolean;
  maskClosable?: boolean;
  keyboard?: boolean;
  onCancel?: () => void;
  destroyOnHidden?: boolean;
  rootClassName?: string;
  classNames?: SectionClassNames;
}

function useBodyScrollLock(active: boolean) {
  useEffect(() => {
    if (!active || typeof document === "undefined") {
      return;
    }

    const { overflow } = document.body.style;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = overflow;
    };
  }, [active]);
}

export function FullScreenModal(props: FullScreenModalProps) {
  const {
    children,
    open = false,
    maskClosable = true,
    keyboard = true,
    onCancel,
    destroyOnHidden = false,
    rootClassName,
    classNames: customClassNames,
  } = props;

  useBodyScrollLock(open);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [shouldRender, setShouldRender] = useState(open || !destroyOnHidden);

  useEffect(() => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }

    if (open) {
      setShouldRender(true);
      return;
    }

    if (!destroyOnHidden) {
      setShouldRender(true);
      return;
    }

    closeTimerRef.current = setTimeout(() => {
      setShouldRender(false);
      closeTimerRef.current = null;
    }, 100);

    return () => {
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
        closeTimerRef.current = null;
      }
    };
  }, [open, destroyOnHidden]);

  useEffect(() => {
    if (!open || !keyboard || typeof document === "undefined") {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onCancel?.();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [keyboard, onCancel, open]);

  const isBrowser = typeof document !== "undefined";

  if (!isBrowser || !shouldRender) {
    return null;
  }

  const handleMaskClick = () => {
    if (maskClosable) {
      onCancel?.();
    }
  };

  return createPortal(
    <div
      data-testid="full-screen-modal"
      data-open={open}
      className={classNames(styles.modalRoot, rootClassName, {
        [styles.hidden]: !open,
      })}
      role="dialog"
      aria-modal="true"
      aria-label="Modal dialog"
    >
      <div
        data-testid="full-screen-modal-mask"
        className={twMerge(styles.modalMask, customClassNames?.mask)}
        onClick={handleMaskClick}
        aria-hidden="true"
      />

      <ScrollView
        className={twMerge(styles.modalContent, customClassNames?.content)}
      >
        {children}
      </ScrollView>
    </div>,
    document.body
  );
}
